import { studyQueryKeys } from '@/config/queryKeys';
import { PROTEOFORM_DEFAULT_THRESHOLD_CPM, TABLE_PAGE_SIZE_OPTIONS } from '@/constants';
import { useProteoformResultsTableConfig } from '@/hooks/useAssays';
import { createAgGridDatasource } from '@/lib/createAgGridDatasource';
import {
  PaginatedSampleProteoformsResponse,
  SampleProteoformPublic,
  StudySampleProteoformSearchRequest,
} from '@/types';
import { flushColumnState } from '@/utils/flushColumnState';
import { Loading } from '@nautilusbio/nb-design-system/Loading';
import { Table } from '@nautilusbio/nb-design-system/Table';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { ColDef, GridOptions, ICellRendererParams } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useEffect, useMemo, useRef, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { useSampleTableGrid } from '../../Studies/components/useSampleTableGrid';

import SampleNameCell from './SampleNameCell';
import { buildTauSamplesColDefs, tauEpitopeKeys, tauSamplesSortFields } from './tauSamplesColDefs';
import { useCanOpenSampleDetail } from './useCanOpenSampleDetail';

/** The four header-strip counts the search envelope carries alongside the page of rows. */
export interface TauSamplesSummary {
  numSamples: number;
  numProteoformGroups: number;
  numPtms: number;
  numIsoforms: number;
}

/** Which org/study/assay a set of counts was fetched for. The summary callbacks are stable, so this
 *  is the only thing that tells a response still in flight from a switch apart from a current one. */
export interface TauSamplesSummarySource {
  orgId: string;
  studyId: string;
  assayId: string;
}

interface TauSamplesDetailTableProps {
  orgId: string;
  studyId: string;
  assayId: string;
  /** Lifted by the route so the summary bar's download can read this grid's live sort/filter state. */
  gridRef?: RefObject<AgGridReact<SampleProteoformPublic> | null>;
  /** Must be referentially stable: it feeds the datasource memo, which reinstalls the grid. */
  onSummaryChange?: (summary: TauSamplesSummary, source: TauSamplesSummarySource) => void;
  /** Must be referentially stable, for the same reason. */
  onSummaryError?: (source: TauSamplesSummarySource) => void;
}

const paginationPageSizeKey = (studyId: string) => `study-${studyId}-tau-detail-pageSize`;
const DEFAULT_PAGE_SIZE = 25;
const TABLE_ID = 'tau-samples-detail-table';

const gridOptions: GridOptions = {
  cacheBlockSize: 100,
  maxBlocksInCache: 10,
  suppressMovableColumns: false,
  suppressDragLeaveHidesColumns: false,
};

export const TauSamplesDetailTable = function TauSamplesDetailTable({
  orgId,
  studyId,
  assayId,
  gridRef: externalGridRef,
  onSummaryChange,
  onSummaryError,
}: TauSamplesDetailTableProps) {
  const { t } = useTranslation(['studies', 'general']);
  const gridRef = useRef<AgGridReact<SampleProteoformPublic>>(null);
  // Mirrored into the caller's ref on mount rather than merging the two into one value, so `gridRef`
  // stays a plain useRef — the grid api is read inside effects, which only exempts real refs.
  const setGridRef = useCallback(
    (instance: AgGridReact<SampleProteoformPublic> | null) => {
      gridRef.current = instance;
      if (externalGridRef) externalGridRef.current = instance;
    },
    [externalGridRef],
  );

  // A hardcoded column list could name a key the assay panel never declares — a permanently
  // blank cell, and a rejected sort or filter. The panel is what the backend validates against.
  const {
    data: tableConfig,
    isError: isTableConfigError,
    isPending: isTableConfigPending,
  } = useProteoformResultsTableConfig(assayId);
  const thresholdCpm = tableConfig?.default_threshold_cpm ?? PROTEOFORM_DEFAULT_THRESHOLD_CPM;
  const epitopeKeys = useMemo(() => tauEpitopeKeys(tableConfig), [tableConfig]);
  const validSortFields = useMemo(() => tauSamplesSortFields(tableConfig), [tableConfig]);

  // Memoised because the datasource memo depends on it: a fresh object per render would reinstall
  // the grid's datasource every render.
  const summarySource = useMemo<TauSamplesSummarySource>(
    () => ({ orgId, studyId, assayId }),
    [orgId, studyId, assayId],
  );

  const datasource = useMemo(
    () =>
      createAgGridDatasource<
        SampleProteoformPublic,
        PaginatedSampleProteoformsResponse,
        StudySampleProteoformSearchRequest
      >(
        studyQueryKeys.endpoints.proteoformsSearch(orgId, studyId),
        ({ limit, skip, sortBy, sortOrder, filters }) => {
          const validSortBy = sortBy && validSortFields.includes(sortBy) ? sortBy : 'sample_name';
          // The assay is scoped by the required top-level `assay_id`, not by a filter — mirrors
          // AssaySamplesTable. Omitting it is a 422.
          return {
            assay_id: assayId,
            skip,
            limit,
            sort_by: validSortBy as StudySampleProteoformSearchRequest['sort_by'],
            sort_order: (sortOrder ?? 'asc') as 'asc' | 'desc',
            threshold_cpm: thresholdCpm,
            filters: filters as StudySampleProteoformSearchRequest['filters'],
          };
        },
        {
          // Epitope cells are booleans, but AG Grid serialises every set-filter value as a
          // string; without this the API receives "true"/"false" and rejects them.
          booleanFields: epitopeKeys,
          // Totals for this study's samples on this assay — the backend already scopes to
          // `assay_id`, so no extra request and nothing aggregated here. All four ignore
          // `filters`, so narrowing the grid does not move them; only `numSamples` is also
          // independent of `threshold_cpm`, the other three count what clears it.
          onResponse: response =>
            onSummaryChange?.(
              {
                numSamples: response.num_samples,
                numProteoformGroups: response.num_proteoform_groups,
                numPtms: response.num_ptms,
                numIsoforms: response.num_isoforms,
              },
              summarySource,
            ),
          onTotalError: () => onSummaryError?.(summarySource),
        },
      ),
    [
      orgId,
      studyId,
      assayId,
      thresholdCpm,
      epitopeKeys,
      validSortFields,
      summarySource,
      onSummaryChange,
      onSummaryError,
    ],
  );

  const {
    defaultColDef,
    handleFilterChanged,
    handleFirstDataRendered,
    handleGridReady,
    handleModelUpdated,
    handlePaginationChanged,
    pageSize,
  } = useSampleTableGrid<SampleProteoformPublic>({
    studyId,
    tableId: TABLE_ID,
    validSortFields,
    paginationPageSizeKeyFn: paginationPageSizeKey,
    defaultPageSize: DEFAULT_PAGE_SIZE,
    datasource,
    autoHeight: true,
  });

  const canOpenSample = useCanOpenSampleDetail();

  const colDefs: ColDef<SampleProteoformPublic>[] = useMemo(
    () =>
      buildTauSamplesColDefs({
        headers: {
          sampleGroups: t('studies:columnHeaders.sampleGroups'),
          sampleName: t('studies:columnHeaders.sampleName'),
          proteoformGroup: t('studies:columnHeaders.proteoformGroup'),
          abundanceCpm: t('studies:columnHeaders.abundanceCpm'),
        },
        yes: t('general:yes'),
        no: t('general:no'),
        empty: t('general:empty'),
        tableConfig,
        sampleNameCellRenderer: (params: ICellRendererParams<SampleProteoformPublic>) => (
          <SampleNameCell canOpenSample={canOpenSample} data={params.data} />
        ),
      }),
    [t, canOpenSample, tableConfig],
  );

  useEffect(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.setGridOption('serverSideDatasource', datasource);
    }
  }, [datasource]);

  // No panel means no grid, so nothing will ever report the counts — say so rather than
  // leaving the strip above on its loading placeholder forever.
  useEffect(() => {
    if (isTableConfigError) onSummaryError?.(summarySource);
  }, [isTableConfigError, summarySource, onSummaryError]);

  // The grid must not mount before the panel resolves: its threshold goes into the request body
  // and its keys are the sortable/filterable set, so mounting early fetches at the fallback
  // threshold and lets the one-shot grid-ready check reset a persisted epitope sort against a
  // field list that doesn't contain it yet.
  if (isTableConfigPending) return <Loading type="inline" />;
  if (isTableConfigError) return <Text>{t('studies:study.tauSummary.columnConfigUnavailable')}</Text>;

  return (
    <Table
      columnDefs={colDefs}
      defaultColDef={defaultColDef}
      domLayout="autoHeight"
      enableAdvancedFilter={false}
      enableResponsiveResize={false}
      getRowId={(params: { data: SampleProteoformPublic }) => `${params.data.sample_id}-${params.data.proteoform_name}`}
      gridOptions={gridOptions}
      onFilterChanged={handleFilterChanged}
      onFirstDataRendered={handleFirstDataRendered}
      onGridPreDestroyed={e => flushColumnState(e.api, TABLE_ID)}
      onGridReady={handleGridReady}
      onModelUpdated={handleModelUpdated}
      onPaginationChanged={handlePaginationChanged}
      pagination
      paginationPageSize={pageSize}
      paginationPageSizeSelector={TABLE_PAGE_SIZE_OPTIONS}
      ref={setGridRef}
      resizeDebounceMs={150}
      rowModelType="serverSide"
      tableId={TABLE_ID}
    />
  );
};
